import { MAX_VALID_DATE_YEAR, MIN_VALID_DATE_YEAR } from "@/constants/dates";
import { provinces, provincesSimplified } from "@/constants/provinces";
import { AiImportStatus, RevenueCalculationType, Site, TenderState } from "@/graphql/__generated__/graphql";
import { TFunction } from "i18next";
import { KeyboardEvent, RefObject } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormatDateOptions, IntlShape } from "react-intl";

export function formatNumberLocale(
  intl: IntlShape,
  amount: string | number,
  style: "currency" | "decimal" | "percent" | "unit",
  showShortScale?: boolean,
  disableDecimalPlaces?: boolean,
  minDecimalPlaces?: number,
  maxDecimalPlaces?: number
) {
  // Convert the amount to a number if it's a string
  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  // Add short scale suffix
  if (showShortScale) {
    let suffix = "";
    let divisor = 1;

    if (numericAmount >= 1_000_000) {
      suffix = "M";
      divisor = 1_000_000;
    } else if (numericAmount >= 1_000) {
      suffix = "K";
      divisor = 1_000;
    }

    const formattedWithSuffix = (numericAmount / divisor).toFixed(
      disableDecimalPlaces ? 0 : maxDecimalPlaces ?? 1
    ) + suffix;
    return "€" + formattedWithSuffix;
  }

  // Format the number using formatNumber
  const formattedNumber = intl.formatNumber(numericAmount, {
    style: style,
    currency: "EUR",
    currencyDisplay: "symbol",
    minimumFractionDigits: disableDecimalPlaces ? 0 : (style === "percent" || style === "decimal") ? minDecimalPlaces ?? 2 : undefined,
    maximumFractionDigits: disableDecimalPlaces ? 0 : (style === "percent" || style === "decimal") ? maxDecimalPlaces ?? 2 : undefined,
    useGrouping: true,
  });

  return formattedNumber;
}

export function formatDateLocale(intl: IntlShape, date: Date | string, options?: FormatDateOptions): string {
  const userLocale = intl.locale;
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return "";
  }

  try {
    return Intl.DateTimeFormat(userLocale, {
      day: options ? options.day : "2-digit",
      month: options ? options.month : "2-digit",
      year: options ? options.year : "numeric",
      ...options
    }).format(dateObj);
  } catch {
    return "";
  }
}

/**
 * Normalizes a date to UTC midnight to avoid timezone issues.
 * This function extracts the year, month, and day from the input date
 * and creates a new Date object set to UTC midnight (00:00:00+00:00).
 *
 * @param date - The date to normalize (can be Date, string, or undefined)
 * @returns A new Date object set to UTC midnight, or null if input is invalid/undefined
 *
 * @example
 * // Returns a Date object representing 2023-04-15T00:00:00+00:00
 * normalizeDateToUtcMidnight(new Date(2023, 3, 15, 14, 30, 0));
 *
 * @example
 * // Returns null for invalid input
 * normalizeDateToUtcMidnight(undefined);
 */
export function normalizeDateToUtcMidnight(date: Date | string | undefined): Date | null {
  if (!date) return null;

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return null;
  }

  // Extract year, month, and day components
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  // Create a new Date object set to UTC midnight
  const utcDate = new Date(`${year}-${month}-${day}T00:00:00+00:00`);
  const utcYear = utcDate.getUTCFullYear();

  if (isNaN(utcDate.getTime()) || utcYear < MIN_VALID_DATE_YEAR || utcYear > MAX_VALID_DATE_YEAR) {
    return null;
  }

  return utcDate;
}

export async function copyTextToClipboard(text: string) {
  const writeTextToClipboard = await navigator.clipboard.writeText(text);
  return writeTextToClipboard;
}

/**
 * Helper function to convert a string to normal-case
 * @param input string
 * @returns
 */
export function toNormalCase(input: string): string | undefined {
  if (!input) return;
  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();
}

/**
 * Helper function to convert a string to Pascal Case
 * @param input string
 * @returns camelCase string
 */
export function toPascalCase(input: string): string | undefined {
  if (!input) return;

  const words = input.split(" ");
  const camelCaseWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return camelCaseWords.join(" ");
}

/**
 * Helper function to convert a string to Title Case
 * @param input string
 * @returns TitleCase string
 */
export const toTitleCase = (input: string): string => {
  return input
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Helper function to convert a string to camelCase
 * @param input string
 * @returns camelCase string
 */
export function toCamelCase(input: string): string | undefined {
  if (!input) return;

  const words = input.split(" ");
  const camelCaseWords = words.map((word, index) => {
    if (index === 0) {
      return word.toLowerCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return camelCaseWords.join("");
}

/**
 * Helper function to convert a camelCase or PascalCase string to a readable format
 * @param input string
 * @returns sentence string
 */
export function toSentenceCase(input: string): string | undefined {
  if (!input) return;

  // Insert spaces before uppercase letters and convert the whole string to lowercase
  const spacedString = input.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

  // Capitalize the first letter of each word
  const words = spacedString.split(" ");
  const capitalizedWords = words.map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  // Join the words with spaces
  return capitalizedWords.join(" ");
}

/**
 * Converts any enum value to a user-friendly text format.
 * - Capitalizes the first letter of each word.
 * - Removes underscores between words.
 *
 * @param {T} enumValue - The value from any enum.
 * @returns {string} A user-friendly version of the enum value with proper capitalization.
 *
 * @example
 *  enum MyEnum {
 *    Some_Value = "Some_Value",
 *    Another_Value = "Another_Value",
 *  }
 *
 *  console.log(enumToClientText(MyEnum.Some_Value));
 *  // Output: "Some Value"
 *
 *  console.log(enumToClientText(MyEnum.Another_Value));
 *  // Output: "Another Value"
 */
export const enumToClientText = <T extends string>(enumValue: T): string => {
  return enumValue
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * This functon checks for the Tailwind CSS breakpoint "md"
 * @returns true if the current breakpoint is not mobile
 */
export const isMdBreakpoint = (): boolean => {
  const mdBreakpointMin = 768;
  const windowWidth = window.innerWidth;

  return windowWidth >= mdBreakpointMin;

};

/**
 * This function checks for the Tailwind CSS breakpoint "lg"
 * @returns true if the current breakpoint is "lg" or larger
 */
export const isLgBreakpoint = (): boolean => {
  const lgBreakpointMin = 1024;
  const windowWidth = window.innerWidth;

  return windowWidth >= lgBreakpointMin;
};

/**
 * This function sorts an array of objects by a given field and order
 * @param sortedData Array of objects
 * @param sortField Field of object to sort by (supports nested fields separated by dots)
 * @param sortOrder "asc" || "desc"
 */
export function sortByField(sortedData: any[], sortField: string, sortOrder: string) {
  if (sortField && sortOrder) {
    sortedData.sort((a, b) => {
      const fieldArray = sortField.split(".");
      let aValue = a;
      let bValue = b;

      // Traverse the nested properties
      for (const field of fieldArray) {
        aValue = aValue ? aValue[field] : undefined;
        bValue = bValue ? bValue[field] : undefined;
      }

      let comparison = 0;

      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      if (sortOrder === "desc") {
        comparison *= -1;
      }

      return comparison;
    });
  }
  return sortedData;
}

/**
 * Sorts an array of option-like objects alphabetically by their `label` (case-insensitive),
 * returning a new array and leaving the input array unmodified.
 *
 * @param options - Array of objects that contain a `label` string.
 * @returns A new array sorted by `label` ascending.
 *
 * @example
 * sortOptionsByLabel([
 *   { label: "Bravo", value: 2 },
 *   { label: "alpha", value: 1 },
 * ]);
 * // => [{ label: "alpha", value: 1 }, { label: "Bravo", value: 2 }]
 */
export function sortOptionsByLabel<T extends { label: string }>(options: T[]): T[] {
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

/**
 * Converts an input in BasisPoints to a percentage.
 * @param input - The value in BasisPoints (bps).
 * @returns The equivalent percentage as a decimal.
 */
export function convertBpToDecimal(input: number): number {
  if (typeof input !== "number") {
    throw new Error("Input must be a number");
  }
  return input / 100; // Divide by 100 to convert to percentage
}

/**
 * Converts an input in BasisPoints to a percentage.
 * @param input - The value in BasisPoints (bps).
 * @returns The equivalent percentage as a decimal.
 */
export function convertBpToDecimalOrUndefined(input: number | undefined): number | undefined {
  if (typeof input !== "number") {
    return undefined;
  }
  return input / 100; // Divide by 100 to convert to percentage
}

/**
 * Converts an input in BasisPoints to a percentage decimal. such that 1400bp = 0.14
 * @param input - The value in BasisPoints (bps).
 * @returns The equivalent percentage as a decimal.
 */
export function convertBpToPercentageDecimal(input: number): number {
  if (typeof input !== "number") {
    throw new Error("Input must be a number");
  }
  return input / 10000; // Divide by 10000 to convert to percentage
}

/**
 * Converts an input in percentage decimal to a decimal. such that 0.14 = 14
 * @param input - The value in percentage decimal.
 * @returns The equivalent value in decimal.
 */
export function convertPercentageDecimalToDecimal(input: number): number {
  if (typeof input !== "number") {
    throw new Error("Input must be a number");
  }
  return input * 100; // Multiply by 100 to convert to percentage
}

/**
 * Converts an input in percentage to BasisPoints.
 * @param input - The value in percentage.
 * @returns The equivalent value in BasisPoints (bps).
 */
export function convertDecimalToBp(input: number): number {
  if (typeof input !== "number") {
    throw new Error("Input must be a number");
  }
  return parseInt((input * 100).toFixed(0)); // Multiply by 100 to convert to BasisPoints
}

/**
   * Function to handle tabbing and looping focus of input fields
   * @param {KeyboardEvent} e
   * @param {RefObject<HTMLInputElement>[]} inputRefs
   */
export const handleTabKey = (e: KeyboardEvent<HTMLInputElement>, inputRefs: RefObject<HTMLInputElement>[]) => {
  const { key, target } = e;

  if (key === "Tab") {
    e.preventDefault();
    const currentIndex = inputRefs.findIndex(ref => ref.current === target);
    const nextIndex = (currentIndex + 1) % inputRefs.length; // Loop back to the first input
    inputRefs[nextIndex].current?.focus();
  }
};

/**
/**
 * Function to get user-friendly string for a given TenderState
 * @param {t} t - The translation function
 * @param {TenderState} state - The state to convert
 * @returns {string} - The user-friendly string
 */
export const getUserFriendlyTenderState = (t: TFunction, state: TenderState): string => {
  const stateToUserFriendlyString: { [_key in TenderState]: string } = {
    [TenderState.Editing]: t("common:tenderstate.editing"),
    [TenderState.Lost]: t("common:tenderstate.lost"),
    [TenderState.PendingMarginDecision]: t("common:tenderstate.pendingmarginapproval"),
    [TenderState.ReadyToSubmit]: t("common:tenderstate.readytosubmit"),
    [TenderState.Submitted]: t("common:tenderstate.submitted"),
    [TenderState.Won]: t("common:tenderstate.won"),
    [TenderState.NotBidding]: t("common:tenderstate.notbidding"),
    [TenderState.PendingBidDecision]: t("common:tenderstate.pendingbiddecision"),
    [TenderState.Annulment]: t("common:tenderstate.annulment"),
  };

  return stateToUserFriendlyString[state] || t("common:tenderstate.unknownstate");
};

/**
 * Function to get province name from code
 * @param code province code
 * @returns province name or code if name is not found
 */
export const getProvinceNameByCode = (code: string, useSimplified: boolean = false): string => {
  const province = useSimplified ? provincesSimplified.find(province => province.code === code) : provinces.find(province => province.code === code);
  return province ? province.name : code;
};

/**
 * Appends the address of a site
 * @param {Site} site - The site object
 * @returns {string} - The address of the site
 */
export const appendSiteAddress = (site: Partial<Site>): string => {
  const provinceName = site.province ? getProvinceNameByCode(site.province) : "";
  return `${site?.address}, ${site?.city}, ${provinceName}, ${site?.country}, ${site?.postalCode}`;
};

/**
 * Converts the AIImportStatus to a percentage for the progress bar
 * @param {AiImportStatus} status
 * @returns {number} The percentage value
 */
export const aiImportStatusToPercentage = (status: AiImportStatus): number => {
  // Define the order of statuses, excluding 'Pending'.
  const statuses = [
    AiImportStatus.WritingExecutiveSummary, // 33%
    AiImportStatus.ExtractingDataFields,  // 66%
    AiImportStatus.Success,                // 100%
    AiImportStatus.Failed                  // 0% in failure case
  ];

  // Calculate percentage based on position in the array (33% for each step)
  const statusIndex = statuses.indexOf(status);

  if (statusIndex === -1) {
    // If status is not found or is 'Pending', return 0
    return 0;
  }

  return status === AiImportStatus.Failed ? 0 : Math.floor((statusIndex + 1) / 3 * 100);
};

/**
 * Converts the AIImportStatus to a background color for the progress bar
 * @param {AiImportStatus} status
 * @returns {string} The background color for the progress bar
 */
export const aiImportStatusBgColour = (status: AiImportStatus): string => {
  switch (status) {
  case AiImportStatus.Pending:
  case AiImportStatus.ExtractingDataFields:
  case AiImportStatus.WritingExecutiveSummary:
    return "bg-blue-500/20";
  case AiImportStatus.Success:
    return "bg-green-500/20";
  case AiImportStatus.Failed:
    return "bg-red-500/20";
  default:
    return "bg-gray-500/20";
  }
};


/**
 * Checks if all AI import statuses are either successful or failed.
 *
 * @param aiImportStatuses - An array of objects containing the status of AI imports.
 * @returns `true` if all imports are either successful or failed, `false` if any import is still in progress.
 */
export const areAllImportsDone = (aiImportStatuses: Array<{ status: AiImportStatus }>) => {
  for (const importStatus of aiImportStatuses) {
    if (importStatus.status !== AiImportStatus.Success && importStatus.status !== AiImportStatus.Failed) {
      return false;  // Return early if an in-progress import is found
    }
  }
  return true;  // All imports are done
};

/**
 * Function to get the nested error from a form error object
 * @param errors - The form error object
 * @param path - The path to the nested error
 * @returns The nested error or null if not found
 */
export const getNestedError = (errors: any, path: string) => {
  return path.split(".").reduce((obj, key) => (obj && obj[key] ? obj[key] : null), errors);
};


export const cleanUpRevenueComponentDataForSubmission = (data: any, revenueCalculationType: RevenueCalculationType) => {
  if (revenueCalculationType === RevenueCalculationType.Margin) {
    delete data.flatRate;
    delete data.hourlyRateAbsolute;
    delete data.hourlyRateRelative;

    // Apply conversion to margin
    if (data.margin) {
      data.margin = convertDecimalToBp(data.margin);
    }
  } else if (revenueCalculationType === RevenueCalculationType.Flatrate) {
    delete data.margin;
    delete data.hourlyRateAbsolute;
    delete data.hourlyRateRelative;
  } else if (revenueCalculationType === RevenueCalculationType.Hourlyrateabsolute) {
    delete data.margin;
    delete data.flatRate;
    delete data.hourlyRateRelative;
  } else if (revenueCalculationType === RevenueCalculationType.Hourlyraterelative) {
    delete data.margin;
    delete data.flatRate;
    delete data.hourlyRateAbsolute;

    // Apply conversion to specific fields in hourlyRateRelative
    if (data.hourlyRateRelative) {
      const fieldsToConvert = [
        "hourlyRateNightRelative",
        "hourlyRateSundayRelative",
        "hourlyRateSundayNightRelative",
        "hourlyRateHolidayRelative",
        "hourlyRateHolidayNightRelative",
      ] as const;

      fieldsToConvert.forEach((field) => {
        const value = data.hourlyRateRelative?.[field];
        if (value !== undefined && value !== null) {
          if (data.hourlyRateRelative) {
            data.hourlyRateRelative[field] = convertDecimalToBp(value);
          }
        }
      });
    }
  }
};

/**
 * Scrolls the referenced HTMLDivElement to the bottom.
 *
 * @param {React.RefObject<HTMLDivElement | null>} ref - A React ref object pointing to an HTMLDivElement.
 */
export const scrollToBottom = (ref: React.RefObject<HTMLDivElement | null>) => {
  if (ref.current) {
    ref.current.scrollTop = ref.current.scrollHeight;
  }
};

/**
 * Function to append query parameters to the URL if an operationalOrganizationId is present
 * @returns {string} - The query parameters to append to the URL
 */
export const appendOpOrgIdParams = (currentOperationalOrganizationID: string | null): string => {
  return currentOperationalOrganizationID ? `?operationalOrganizationId=${currentOperationalOrganizationID}` : "";
};

/**
 * Handles the keydown event on the input field.
 *
 * When the "Enter" key is pressed and the form is valid, this function prevents
 * the default action and opens the modal dialog by setting the open state to true.
 *
 * @param {React.KeyboardEvent<HTMLInputElement>} e - The keyboard event triggered by the input field.
 * @param {UseFormReturn<any>} form - The form object.
 * @param {(_open: boolean) => void} setOpen - The function to set the open state of the modal dialog.
 * @returns {void}
 */
export const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, form: UseFormReturn<any>, setOpen: (_open: boolean) => void): void => {
  if (e.key === "Enter") {
    if (e.shiftKey) {
      // Allow default behavior for Shift+Enter (new line)
      return;
    }
    if (form.formState.isValid) {
      e.preventDefault();
      return setOpen(true);
    }
  }
};

// START: Tender State Colour
/**
 * @typedef {"orange" | "red" | "yellow" | "teal" | "purple" | "green" | "gray" | "slate"} StateColor
 * Represents the possible colors for tender states
 */
type StateColor = "orange" | "red" | "yellow" | "teal" | "purple" | "green" | "gray" | "slate";

/**
 * @type {Record<TenderState, StateColor>}
 * Maps each tender state to a specific color
 */
const STATE_COLOR_MAP: Record<TenderState, StateColor> = {
  [TenderState.Editing]: "orange",
  [TenderState.Lost]: "red",
  [TenderState.PendingMarginDecision]: "yellow",
  [TenderState.ReadyToSubmit]: "teal",
  [TenderState.Submitted]: "purple",
  [TenderState.Won]: "green",
  [TenderState.NotBidding]: "gray",
  [TenderState.PendingBidDecision]: "yellow",
  [TenderState.Annulment]: "slate"
} as const;

/**
 * @type {Record<StateColor, { bg: string; border: string; text: string }>}
 * Maps each color to its corresponding Tailwind CSS classes for background, border, and text
 */
const COLOR_CLASSES: Record<StateColor, { bg: string; border: string; text: string }> = {
  orange: { bg: "bg-orange-500", border: "border-orange-500", text: "text-orange-500" },
  red: { bg: "bg-red-500", border: "border-red-500", text: "text-red-500" },
  yellow: { bg: "bg-yellow-500", border: "border-yellow-500", text: "text-yellow-500" },
  teal: { bg: "bg-teal-500", border: "border-teal-500", text: "text-teal-500" },
  purple: { bg: "bg-purple-500", border: "border-purple-500", text: "text-purple-500" },
  green: { bg: "bg-green-500", border: "border-green-500", text: "text-green-500" },
  gray: { bg: "bg-gray-500", border: "border-gray-500", text: "text-gray-500" },
  slate: { bg: "bg-slate-500", border: "border-slate-500", text: "text-slate-500" }
} as const;

/**
  * Determines the appropriate bg colour based on the tender state.
  *
  * @param {TenderState} state - The current state of the tender.
  * @param {boolean} bg - Whether to include the bg colour.
  * @param {boolean} border - Whether to include the border colour.
  * @param {boolean} text - Whether to include the text colour.
  * @returns {string} The CSS class corresponding to the tender state colour.
  */
export const handleTenderStateColour = (state: TenderState, bg?: boolean, border?: boolean, text?: boolean): string => {
  const color = STATE_COLOR_MAP[state] || "gray";
  const colorClasses = COLOR_CLASSES[color];

  const classes = [
    bg && colorClasses.bg,
    border && colorClasses.border,
    text && colorClasses.text
  ].filter(Boolean);

  return classes.join(" ");
};
// END: Tender State Colour

/**
 * Sorts pricing details by shift type in the standard order:
 * cumulated → day → night → sunday → sunday_night → holiday → holiday_night
 *
 * @param {Array} items - Array of pricing objects with a 'type' field (ShiftType)
 * @returns {Array} A new sorted array
 *
 * @example
 * const sorted = sortByShiftType(priceObject.pricing.servicePricing);
 * // Result: Items sorted by the predefined shift type order
 */
export function sortByShiftType<T extends { type: string }>(items: T[]): T[] {
  const ORDER = ["cumulated", "day", "night", "sunday", "sunday_night", "holiday", "holiday_night"];

  return [...items].sort((a, b) => {
    const aIndex = ORDER.indexOf(a.type.toLowerCase());
    const bIndex = ORDER.indexOf(b.type.toLowerCase());

    // If both types are in the order array, sort by position
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // Items in the order array come before items not in the array
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // Maintain original order for items not in the array
    return 0;
  });
}

/**
 * Converts a Service to a standardized PriceObject for rendering.
 *
 * @param {Service} service - The service to convert
 * @param {number} contractMonthsDuration - The duration of the contract in months
 * @returns {PriceObject} The standardized price object
 *
 * @example
 * const priceObject = convertServiceToPriceObject(service, 12);
 */
export function convertServiceToPriceObject(
  service: any,
  contractMonthsDuration: number
): PriceObject {
  const cumulatedPricing = service.pricing.servicePricing.find(
    (p: any) => p.type === "CUMULATED"
  );

  return {
    id: service.id,
    name: service.name,
    totalEmployeeCount: service.pricing.totalEmployeeCount,
    revenue: cumulatedPricing ? Number(cumulatedPricing.revenue) : 0,
    monthlyRate: contractMonthsDuration > 0 ? service.pricing.monthlyRate : 0,
    yearlyRate: contractMonthsDuration > 0 ? service.pricing.yearlyRate : 0,
    pricePerHour: cumulatedPricing ? Number(cumulatedPricing.pricePerHour) : 0,
    type: service.type,
    pricing: service.pricing,
    margin: convertBpToDecimal(service.margin),
    minutes: {
      cumulated: service.minutesTotal,
      day: service.minutesDay ?? undefined,
      night: service.minutesNight ?? undefined,
      sunday: service.minutesSunday ?? undefined,
      sunday_night: service.minutesSundayNight ?? undefined,
      holiday: service.minutesHoliday ?? undefined,
      holiday_night: service.minutesHolidayNight ?? undefined,
    },
    hasCustomSchedule: service.pricing.hasCustomSchedule,
    hoursBriefing: service.hoursBriefingPerEmployee ?? undefined,
    transferredEmployeePercentage:
      service.type === "PERMANENT"
        ? service.transferredEmployeePercentage
        : null,
    pauseIncluded:
      service.type === "PERMANENT"
        ? service.pauseIncluded
        : null,
  };
}

/**
 * Converts TenderPriceDetails to a standardized PriceObject for rendering.
 *
 * @param {TenderPriceDetails} tenderPriceDetails - The tender price details to convert
 * @returns {PriceObject} The standardized price object
 *
 * @example
 * const priceObject = convertTenderToPriceObject(tenderPriceDetails);
 */
export function convertTenderToPriceObject(
  tenderPriceDetails: any
): PriceObject {
  return {
    id: tenderPriceDetails.id,
    name: tenderPriceDetails.name,
    surcharge: convertBpToDecimal(tenderPriceDetails.surcharge),
    revenue: tenderPriceDetails.revenue,
    pricePerHour: tenderPriceDetails.pricePerHour,
    margin: convertBpToDecimal(tenderPriceDetails.margin),
    monthlyRate: tenderPriceDetails.monthlyRate,
    yearlyRate: tenderPriceDetails.yearlyRate,
    totalEmployeeCount: tenderPriceDetails.totalEmployeeCount,
    minutes: {
      cumulated: tenderPriceDetails.minutes,
    },
  };
}
